import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
// styles
import styles from "./ArbitratorTable.module.scss";
// components
import Table from "../../common/Table";

// packages
import { useTranslation } from "react-i18next";

// types
import {
  ESenderRole,
  IContractDetailsResponseData,
  UserProfile,
} from "../../../setup/api/services/contract/models";
import ContractActions from "../../../pages/contract/actions";
import { useParams } from "react-router-dom";
import { ContractContext } from "../../../providers/contract";
import { EPARTY_STATUS } from "../../../enums";
import { MilestonePartyStatus } from "../partyStatus";

interface ArbitratorTableProps {
  partiesObj: Partial<UserProfile>;
  canMakeContractActions: (userId) => boolean | undefined;
  milestone: IContractDetailsResponseData;
}

export type RowData = {
  id: string;
  name: string;
  number: string;
  amount: string;
  receive: string;
  status: string;
  actions: () => any;
};

const ArbitratorTable: React.FC<ArbitratorTableProps> = ({
  partiesObj,
  milestone,
  canMakeContractActions,
}) => {
  const { t } = useTranslation();
  const { actions } = useContext(ContractContext);
  const { id } = useParams();
  const [rows, setRows] = useState<RowData[]>([]);

  const parties = useMemo(
    () => milestone?.partiesProfiles?.BROKER,
    [milestone]
  );

  const refreshContractDetails = useCallback(() => {
    if (id) actions?.getContract();
  }, [id]);

  const columns = [
    { type: "id", value: "" },
    { type: "name", value: `${t("lbl_party")}` },
    { type: "number", value: `${t("lbl_edit_profile_phone")}` },
    { type: "amount", value: `${t("lbl_value_sar")}` },
    { type: "receive", value: `${t("lbl_loyal_receive")}` },
    { type: "arbitration", value: `${t("lbl_arbitration")}` },
    { type: "action", value: "" },
  ];

  const transformPayToRows = useCallback(
    (parties): RowData[] => {
      return parties
        ?.filter((party) => !!partiesObj[`${party?.phoneNumber}`]?.arbitrator)
        ?.map((item, index) => ({
          id: `${index + 1}.`,
          name: item?.profile?.fullName || t("lbl_not_registered_yet"),
          number: item?.phoneNumber,
          amount: partiesObj[`${item?.phoneNumber}`]?.amount,
          receive: partiesObj[`${item?.phoneNumber}`]?.amount,
          status: partiesObj[`${item?.phoneNumber}`]?.status,
          action: "",
        }));
    },
    [partiesObj, t]
  );

  useEffect(() => {
    if (parties?.length) {
      setRows(transformPayToRows(parties));
    }
  }, [parties, parties?.length, transformPayToRows]);

  const renderCell = useCallback(
    (column: string, row: unknown) => {
      const rowData = row as RowData;
      switch (column) {
        case "id":
          return (
            <p className={`${styles["p3"]} ${styles["g600"]}`}>{rowData?.id}</p>
          );
        case "name":
          return (
            <p className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.name}
            </p>
          );
        case "number":
          return (
            <p
              style={{ direction: "ltr" }}
              className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.number}
            </p>
          );
        case "amount":
          return (
            <p className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.amount}
            </p>
          );
        case "receive":
          return (
            <p className={`${styles["p3"]} ${styles["g900"]}`}>
              {rowData?.receive}
            </p>
          );
        case "arbitration":
          return (
            <div>
              <MilestonePartyStatus party={row as Partial<UserProfile>} />
            </div>
          );

        case "action":
          if (
            rowData?.status === EPARTY_STATUS.ARBITRATOR_ACCEPT_ITEM ||
            rowData?.status === EPARTY_STATUS.ARBITRATOR_REFUNDED_CUSTOMER
          )
            return <></>;
          return (
            <div
              className={styles["action_container"]}
              style={{ padding: "8px" }}>
              {canMakeContractActions(
                parties?.find((party) => party?.phoneNumber == rowData?.number)
              ) && (
                <ContractActions
                  receiverType={ESenderRole.BROKER}
                  handleMenuClose={refreshContractDetails}
                  contract={{
                    ...milestone,
                    receiverId: milestone?.receiverId || "-1",
                    senderId: `${
                      parties?.find(
                        (party) => party?.phoneNumber === rowData?.number
                      )?.id
                    }`,
                  }}
                  id={"arbitrating-table"}
                />
              )}
            </div>
          );
        default:
          return <p>{rowData[column]}</p>;
      }
    },
    [canMakeContractActions, milestone, parties, refreshContractDetails]
  );

  return <Table columns={columns} rows={rows} renderCell={renderCell} />;
};

export default React.memo(ArbitratorTable);
